// src/shared/utils/analytics/posthog.js
// PostHog analytics for user profiles, session recording, and error tracking

import posthog from 'posthog-js';
import logger from '../core/logger';

/**
 * Initialize PostHog analytics (called when not using PostHogProvider)
 * Note: If using PostHogProvider in main.jsx, this is not needed
 */
export function initPostHog() {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  if (!apiKey) {
    if (import.meta.env.DEV) {
      logger.debug('[PostHog] No API key found, skipping initialization');
    }
    return;
  }

  // Check if already initialized (PostHogProvider may have initialized it)
  if (posthog.__loaded) {
    logger.debug('[PostHog] Already initialized');
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      defaults: '2025-11-30',

      // Autocapture settings
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,

      // Session recording with privacy settings
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-mask]',
      },

      // Privacy settings
      mask_all_text: false,
      mask_all_element_attributes: false,

      loaded: (ph) => {
        if (import.meta.env.DEV) {
          logger.debug('[PostHog] Initialized successfully');
        }
      },

      // Performance
      request_batching: true,
    });
  } catch (error) {
    logger.error('[PostHog] Initialization failed:', error);
  }
}

/**
 * Check if PostHog is loaded and ready
 */
export function isPostHogReady() {
  return typeof posthog !== 'undefined' && posthog.__loaded;
}

/**
 * Identify a user (call after login)
 * @param {Object} user - Firebase user object
 * @param {Object} properties - Additional user properties
 */
export function identifyUser(user, properties = {}) {
  if (!isPostHogReady()) return;

  try {
    posthog.identify(user.uid, {
      email: user.email,
      name: user.displayName,
      email_verified: user.emailVerified,
      provider: user.providerData?.[0]?.providerId || 'unknown',
      created_at: user.metadata?.creationTime,
      ...properties,
    });

    if (import.meta.env.DEV) {
      logger.debug('[PostHog] User identified:', user.uid);
    }
  } catch (error) {
    logger.error('[PostHog] Failed to identify user:', error);
  }
}

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} properties - Event properties
 */
export function trackPostHogEvent(eventName, properties = {}) {
  if (!isPostHogReady()) return;

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    logger.error('[PostHog] Failed to track event:', error);
  }
}

/**
 * Set user properties (without identifying)
 * @param {Object} properties - Properties to set on the user
 */
export function setPostHogUserProperties(properties) {
  if (!isPostHogReady()) return;

  try {
    posthog.people.set(properties);
  } catch (error) {
    logger.error('[PostHog] Failed to set user properties:', error);
  }
}

/**
 * Set user properties once (only if not already set)
 * @param {Object} properties - Properties to set once
 */
export function setPostHogUserPropertiesOnce(properties) {
  if (!isPostHogReady()) return;

  try {
    posthog.people.set_once(properties);
  } catch (error) {
    logger.error('[PostHog] Failed to set user properties once:', error);
  }
}

/**
 * Track an exception/error
 * @param {Error} error - The error object
 * @param {Object} context - Additional context
 */
export function trackException(error, context = {}) {
  if (!isPostHogReady()) return;

  try {
    posthog.capture('$exception', {
      $exception_message: error.message,
      $exception_stack: error.stack,
      $exception_type: error.name,
      ...context,
    });
  } catch (e) {
    logger.error('[PostHog] Failed to track exception:', e);
  }
}

/**
 * Add a breadcrumb for context (useful for debugging user sessions)
 * @param {string} category - Category of the breadcrumb
 * @param {string} message - Breadcrumb message
 * @param {Object} data - Additional data
 */
export function addBreadcrumb(category, message, data = {}) {
  if (!isPostHogReady()) return;

  try {
    posthog.capture('$breadcrumb', {
      category,
      message,
      ...data,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    logger.error('[PostHog] Failed to add breadcrumb:', error);
  }
}

/**
 * Reset user identity (call on logout)
 */
export function resetPostHogUser() {
  if (!isPostHogReady()) return;

  try {
    posthog.reset();
    if (import.meta.env.DEV) {
      logger.debug('[PostHog] User reset');
    }
  } catch (error) {
    logger.error('[PostHog] Failed to reset user:', error);
  }
}

/**
 * Start a session recording (if not already recording)
 */
export function startSessionRecording() {
  if (!isPostHogReady()) return;

  try {
    posthog.startSessionRecording();
  } catch (error) {
    logger.error('[PostHog] Failed to start session recording:', error);
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecording() {
  if (!isPostHogReady()) return;

  try {
    posthog.stopSessionRecording();
  } catch (error) {
    logger.error('[PostHog] Failed to stop session recording:', error);
  }
}

/**
 * Register super properties (sent with every event)
 * @param {Object} properties - Properties to register
 */
export function registerSuperProperties(properties) {
  if (!isPostHogReady()) return;

  try {
    posthog.register(properties);
  } catch (error) {
    logger.error('[PostHog] Failed to register super properties:', error);
  }
}

/**
 * Get the current distinct ID
 */
export function getDistinctId() {
  if (!isPostHogReady()) return null;

  try {
    return posthog.get_distinct_id();
  } catch (error) {
    logger.error('[PostHog] Failed to get distinct ID:', error);
    return null;
  }
}

/**
 * Get feature flag value
 * @param {string} flagKey - The feature flag key
 * @param {*} defaultValue - Default value if flag not found
 */
export function getFeatureFlag(flagKey, defaultValue = false) {
  if (!isPostHogReady()) return defaultValue;

  try {
    return posthog.getFeatureFlag(flagKey) ?? defaultValue;
  } catch (error) {
    logger.error('[PostHog] Failed to get feature flag:', error);
    return defaultValue;
  }
}

/**
 * Check if feature flag is enabled
 * @param {string} flagKey - The feature flag key
 */
export function isFeatureEnabled(flagKey) {
  if (!isPostHogReady()) return false;

  try {
    return posthog.isFeatureEnabled(flagKey);
  } catch (error) {
    logger.error('[PostHog] Failed to check feature flag:', error);
    return false;
  }
}

// Export the raw posthog instance for advanced usage
export { posthog };

export default {
  initPostHog,
  isPostHogReady,
  identifyUser,
  trackPostHogEvent,
  setPostHogUserProperties,
  setPostHogUserPropertiesOnce,
  trackException,
  addBreadcrumb,
  resetPostHogUser,
  startSessionRecording,
  stopSessionRecording,
  registerSuperProperties,
  getDistinctId,
  getFeatureFlag,
  isFeatureEnabled,
  posthog,
};
